import { useState, useEffect, useRef } from "react";
import {
  Paper,
  Input,
  Grid,
  Button,
  Title,
  Flex,
  ActionIcon,
  Box,
  Center,
  Text,
  Checkbox,
  Group,
} from "@mantine/core";
import { IconPlus } from "../../shared/svg/AddIcon";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../store/useProfileStore";
import { Certification } from "../../types/profile-editing";
import {
  addCertification,
  deleteCertification,
  updateCertification,
} from "../../api/profileEditingApi";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import { CheckIcon } from "../../shared/svg/CheckIcon";
import { notifications } from "@mantine/notifications";
import "dayjs/locale/uk";
import "dayjs/locale/en";

import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { TrashCanSmall } from "../../shared/svg/TrashCanSmall";
import { useMediaQuery } from "@mantine/hooks";
import { DateInput, DatesProvider } from "@mantine/dates";
import dayjs from "dayjs";
import { IconCalendar } from "../../shared/svg/IconCalendar";
import { InfoTooltip } from "../../shared/components/InfoTooltip";
import ConfirmDelete from "../../utils/components/ConfirmDelete";

interface Props {
  setChanges: (t: boolean) => void;
}

type CertificationItem = Certification & {
  isNew?: boolean;
  isSaving?: boolean;
  errors?: Record<string, string[]>;
  notExpired?: boolean;
};

const CertificatesEditingWidget: React.FC<Props> = ({ setChanges }) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [certifications, setCertifications] = useState<CertificationItem[]>([]);
  const [originalCertifications, setOriginalCertifications] = useState<
    Certification[]
  >([]);
  const matches = useMediaQuery("(max-width: 650px)");

  const { profile, profileLanguage, setUserMarketChanges } = useProfileStore();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  useEffect(() => {
    dayjs.locale(Object.keys(profileLanguage!)[0]);
  }, [profileLanguage]);

  useEffect(() => {
    if (profile?.certifications.length) {
      const updatedCertifications = profile.certifications.map((cert) => ({
        ...cert,
        notExpired: cert.expiration_date === null,
      }));

      setCertifications(updatedCertifications);
      setOriginalCertifications(updatedCertifications);
    }
  }, [profile]);

  const handleAddCertification = () => {
    const newCertification: CertificationItem = {
      title: "",
      issuer: "",
      credential_id: "",
      credential_url: "",
      issue_date: null,
      expiration_date: null,
      isNew: true,
      notExpired: false,
      errors: {},
    };
    setCertifications((prev) => [...prev, newCertification]);
    setOriginalCertifications((prev) => [...prev, newCertification]);

    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleChange = (
    index: number,
    field: keyof CertificationItem,
    value: any
  ) => {
    setCertifications((prev) =>
      prev.map((cert, i) =>
        i === index
          ? {
              ...cert,
              [field]: value,
              errors: { ...cert.errors, [field]: [] }, // очищаємо помилку для поля
            }
          : cert
      )
    );
  };

  const handleDelete = (index: number, id?: number) => {
    if (id) {
      deleteCertification(id);
      setChanges(true);
      setUserMarketChanges(true);
    }

    setCertifications((prev) => prev.filter((_, i) => i !== index));
    notifications.show({
      title: t("success"),
      message: t("delete_successfully"),
      icon: <TrashCanSmall />,
      color: "#d3d3d3",
      style: {
        border: "1px solid #d3d3d3",
        borderRadius: "8px",
      },
    });
  };

  const isCertificationChanged = (
    current: Certification,
    original?: Certification
  ) => {
    if (!original) return true;

    const keys: (keyof Certification)[] = [
      "title",
      "issuer",
      "credential_id",
      "credential_url",
      "issue_date",
      "expiration_date",
    ];

    return keys.some((key) => current[key] !== original[key]);
  };

  const handleSave = async (index: number, cert: CertificationItem) => {
    setCertifications((prev) => {
      const updated = [...prev];
      updated[index].isSaving = true;
      return updated;
    });

    try {
      const isExisting = Boolean(cert.id);

      const cleanedCert = Object.fromEntries(
        Object.entries(cert).filter(
          ([key]) =>
            !["isNew", "errors", "isSaving", "notExpired"].includes(key)
        )
      ) as Partial<Certification>;

      const errors: Record<string, string[]> = {};

      if (!cleanedCert.title?.trim()) {
        errors.title = [t("field_required")];
      }
      if (cleanedCert.expiration_date && !cleanedCert.issue_date) {
        errors.issue_date = [t("field_required")];
      }

      if (Object.keys(errors).length > 0) {
        setCertifications((prev) => {
          const updated = [...prev];
          updated[index].errors = errors;
          updated[index].isSaving = false;
          return updated;
        });
        return;
      }

      if (!cleanedCert.issue_date) {
        cleanedCert.issue_date = null;
      }
      if (!cert.notExpired && !cleanedCert.expiration_date) {
        cleanedCert.expiration_date = null;
      }

      const payload = {
        ...cleanedCert,
        expiration_date: cert.notExpired ? null : cleanedCert.expiration_date,
      };

      let response = isExisting
        ? await updateCertification(payload)
        : await addCertification(payload);

      setCertifications((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...response,
          notExpired: response.expiration_date === null,
          errors: {},
          isNew: false,
        };
        return updated;
      });
      setOriginalCertifications((prev) => {
        const updated = [...prev];
        updated[index] = response;
        return updated;
      });
      setChanges(true);
      setUserMarketChanges(true);

      if (isExisting && response) {
        notifications.show({
          title: t("success"),
          message: t("changed_successfully"),
          icon: <EditIconSmall />,
          color: "#CEB7FF",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
      } else {
        notifications.show({
          title: t("success"),
          message: t("add_successfully"),
          icon: <CheckIcon />,
          color: "green",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
      }

      setCertifications((prev) => {
        const updated = [...prev];
        updated[index].isSaving = false;
        return updated;
      });
    } catch (error: any) {
      if (error.errors) {
        setCertifications((prev) => {
          const updated = [...prev];
          updated[index].errors = {
            ...updated[index].errors,
            ...error.errors,
          };
          updated[index].isSaving = false;
          return updated;
        });
      } else {
        // setErrorMessage(t("somethingWentWrong"));
      }
    }
  };

  const handleCancel = (index: number) => {
    setCertifications((prev) => {
      const updated = [...prev];
      updated[index] = { ...originalCertifications[index] };
      return updated;
    });
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mb="lg"
        direction={matches ? "column" : "row"}
        gap={matches ? 20 : 10}
      >
        <Title size={36}>{t("certifications")}</Title>
        <Button
          leftSection={<IconPlus />}
          onClick={handleAddCertification}
          variant="light"
          disabled={certifications.some((e) => e.isNew)}
        >
          {t("add_certification")}
        </Button>
      </Flex>

      {certifications.length === 0 ? (
        <Center py="xl">
          <Paper
            radius="md"
            p="xl"
            withBorder
            shadow="sm"
            style={{ maxWidth: 1000, width: "100%" }}
          >
            <Flex
              align="center"
              w="100%"
              px={20}
              gap={matches ? 20 : 40}
              direction={matches ? "column" : "row"}
            >
              <Title
                order={2}
                fw={700}
                w={matches ? "100%" : "33%"}
                ta="left"
                style={{ fontSize: "1.5rem" }}
              >
                {t("nothing_here")}
              </Title>

              <Text
                size="xl"
                c="dimmed"
                w={matches ? "100%" : "66%"}
                style={{ fontSize: "1.125rem" }}
              >
                {t("no_certifications")}
              </Text>
            </Flex>
          </Paper>
        </Center>
      ) : (
        <Flex direction="column" gap={30}>
          {certifications.map((item, index) => {
            const originalCertification =
              originalCertifications.find((cert) => cert.id === item.id) ||
              item;
            return (
              <Paper
                withBorder
                p={24}
                radius="md"
                key={index}
                style={{
                  backgroundColor: "#f5f3fa",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid gutter={16} grow>
                  <Grid.Col span={matches ? 12 : 6}>
                    <Input.Wrapper
                      label={t("title")}
                      withAsterisk
                      error={item.errors?.title?.[0]}
                    >
                      <Input
                        value={item.title}
                        onChange={(e) => {
                          handleChange(index, "title", e.currentTarget.value);
                        }}
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <Grid.Col span={matches ? 12 : 6}>
                    <Input.Wrapper
                      label={t("issuer")}
                      error={item.errors?.issuer?.[0]}
                    >
                      <Input
                        value={item.issuer ?? ""}
                        onChange={(e) =>
                          handleChange(index, "issuer", e.currentTarget.value)
                        }
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <Grid.Col span={matches ? 12 : 6}>
                    <Input.Wrapper
                      label={
                        <Group gap={0} align="center" wrap="nowrap">
                          <Text size="14px">{t("credential_id")}</Text>

                          <InfoTooltip
                            label={t("certificate_id_description")}
                            size={20}
                          />
                        </Group>
                      }
                      error={item.errors?.credential_id?.[0]}
                    >
                      <Input
                        value={item.credential_id ?? ""}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "credential_id",
                            e.currentTarget.value
                          )
                        }
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <Grid.Col span={matches ? 12 : 6}>
                    <Input.Wrapper
                      label={
                        <Group gap={0} align="center" wrap="nowrap">
                          <Text size="14px">{t("credential_url")}</Text>

                          <InfoTooltip
                            label={t("certificate_url_description")}
                            size={20}
                          />
                        </Group>
                      }
                      error={item.errors?.credential_url?.[0]}
                    >
                      <Input
                        value={item.credential_url ?? ""}
                        onChange={(e) => {
                          handleChange(
                            index,
                            "credential_url",
                            e.currentTarget.value
                          );
                        }}
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <DatesProvider
                    settings={{ locale: Object.keys(profileLanguage!)[0] }}
                  >
                    <Grid.Col span={matches ? 12 : 6}>
                      <DateInput
                        label={t("issue_date")}
                        value={
                          item.issue_date ? new Date(item.issue_date) : null
                        }
                        onChange={(date) =>
                          handleChange(
                            index,
                            "issue_date",
                            date ? dayjs(date).format("YYYY-MM-DD") : ""
                          )
                        }
                        maxDate={
                          item.expiration_date
                            ? new Date(item.expiration_date)
                            : undefined
                        }
                        error={item.errors?.issue_date?.[0]}
                        clearable
                        size="md"
                        radius="md"
                        placeholder={t("pick_date")}
                        leftSection={<IconCalendar />}
                        renderDay={(date) => {
                          const isToday = dayjs(date).isSame(new Date(), "day");

                          return (
                            <Box
                              style={{
                                backgroundColor: isToday
                                  ? "#faf0fd"
                                  : "transparent",
                                color: isToday ? "#862e9c" : "inherit",
                                border: isToday ? "1px solid #862e9c" : "none",
                                borderRadius: 6,
                                width: 34,
                                height: 34,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: isToday ? 700 : 400,
                              }}
                            >
                              {date.getDate()}
                            </Box>
                          );
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col span={matches ? 12 : 6}>
                      <DateInput
                        label={t("expiration_date")}
                        value={
                          item.expiration_date
                            ? new Date(item.expiration_date)
                            : null
                        }
                        onChange={(date) =>
                          handleChange(
                            index,
                            "expiration_date",
                            date ? dayjs(date).format("YYYY-MM-DD") : ""
                          )
                        }
                        disabled={item.notExpired}
                        minDate={
                          item.issue_date
                            ? new Date(item.issue_date)
                            : undefined
                        }
                        clearable
                        size="md"
                        radius="md"
                        placeholder={t("pick_date")}
                        leftSection={<IconCalendar />}
                        renderDay={(date) => {
                          const isToday = dayjs(date).isSame(new Date(), "day");

                          return (
                            <Box
                              style={{
                                backgroundColor: isToday
                                  ? "#faf0fd"
                                  : "transparent",
                                color: isToday ? "#862e9c" : "inherit",
                                border: isToday ? "1px solid #862e9c" : "none",
                                borderRadius: 6,
                                width: 34,
                                height: 34,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: isToday ? 700 : 400,
                              }}
                            >
                              {date.getDate()}
                            </Box>
                          );
                        }}
                      />
                    </Grid.Col>
                  </DatesProvider>

                  <Grid.Col span={matches ? 12 : 6}>
                    <div />
                  </Grid.Col>
                  <Grid.Col span={matches ? 12 : 6}>
                    <Checkbox
                      pl={4}
                      label={t("certificate_not_expired")}
                      checked={item.notExpired}
                      onChange={(event) => {
                        handleChange(
                          index,
                          "notExpired",
                          event.currentTarget.checked
                        );
                        setCertifications((prev) =>
                          prev.map((cert, i) =>
                            i === index
                              ? {
                                  ...cert,
                                  expiration_date: null,
                                }
                              : cert
                          )
                        );
                      }}
                    />
                  </Grid.Col>
                </Grid>

                <ActionIcon
                  variant="subtle"
                  color="gray"
                  style={{ position: "absolute", top: 10, right: 10 }}
                  onClick={() => {
                    if (!item.id) {
                      handleDelete(index);
                    } else {
                      setIsDeleteOpen(true);
                      setDeleteId(item.id!);
                      setDeleteIndex(index);
                    }
                  }}
                >
                  <ClearIcon />
                </ActionIcon>

                <Flex
                  align="center"
                  mt={15}
                  justify={matches ? "center" : "end"}
                  gap={20}
                  direction="row"
                >
                  {item?.errors?.non_field_errors && (
                    <Text color="#B00020" size="sm">
                      {item?.errors?.non_field_errors}
                    </Text>
                  )}

                  <Button
                    variant="light"
                    color="gray"
                    style={{ flexShrink: 0 }}
                    onClick={() => {
                      if (!item.isNew) {
                        handleCancel(index);
                      } else {
                        handleDelete(index, item.id);
                      }
                    }}
                    disabled={
                      item.isSaving ||
                      (item.id !== undefined &&
                        !isCertificationChanged(item, originalCertification))
                    }
                  >
                    {t("cancel")}
                  </Button>

                  <Button
                    variant="light"
                    color="green"
                    style={{ flexShrink: 0 }}
                    onClick={() => handleSave(index, item)}
                    disabled={
                      item.isSaving ||
                      (item.id !== undefined &&
                        !isCertificationChanged(item, originalCertification))
                    }
                  >
                    {t("save")}
                  </Button>
                </Flex>
              </Paper>
            );
          })}
          <div ref={scrollRef} />
        </Flex>
      )}

      <ConfirmDelete
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
          setDeleteIndex(null);
        }}
        onDelete={() => {
          if (deleteId !== null && deleteIndex !== null)
            handleDelete(deleteIndex, deleteId);
        }}
        title={t("delete_certification_title")}
      />
    </Box>
  );
};

export default CertificatesEditingWidget;
